<template>
  <div class="export-stack">
    <div :key="exportsData?.length">
      <div
        class="export-stack__container"
        :class="{
          'export-stack__container--expanded': isExpanded,
        }"
        @mouseleave="collapseStack"
      >
        <transition-group
          ref="export-list"
          name="stack"
          tag="div"
          class="export-stack__list"
        >
          <ExportCard
            v-for="(item, index) in exportsData"
            :key="item.id"
            :export-item="item"
            :is-removing="isRemoving(item.id)"
            :loadingRemove="loadingRemove"
            :style="cardStyle(index)"
            @mouseenter="() => {
              if(index === 0) {
                isExpanded = true
              }
            }"
            @mouseenterEl="showExpanded(index)"
            @remove="remove(item.id)"
            @export="runExport(item)"
          />
        </transition-group>
      </div>
    </div>

    <ExportModal
      ref="export-modal"
      :show="show"
      :targetId="referralProgramId"
      @close="$store.commit('modal/SET_CLOSE_MODAL')"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { initEcho, destroyEcho } from '@/services/websockets/echo.js'
import ExportCard from '@/views/apps/referral-program/components/export/ExportCard.vue'
import ExportModal from '@/views/apps/referral-program/components/export/ExportModal.vue'
import exportReferralProgramSocket from '@/services/websockets/exportReferralProgramSocket.js'

export default {
  components: {
    ExportCard,
    ExportModal,
  },
  data() {
    return {
      isExpanded: false,
      loadingRemove: false,
    }
  },
  computed: {
    ...mapGetters('exports', ['exportsData', 'isRemoving']),
    ...mapGetters('modal', ['show']),

    referralProgramId() {
      const id = this.$route.params.id

      return /^\d+$/.test(id)
        ? id
        : null
    },
  },
  watch: {
    referralProgramId: {
      async handler(newId) {
        if (!newId) {
          this.resetState()
          this.unsubscribe()

          return
        }

        await this.getExportsData(newId)

        this.subscribe(newId)
      },
      immediate: true,
    },
    '$route.path'(newPath) {
      if (!newPath.includes('/referral/programs/')) {
        this.resetState()
        this.unsubscribe()
      }
    },
  },
  methods: {
    ...mapActions('exports', [
      'getExportsData',
      'removeExport',
      'downloadExport',
      'resetState',
    ]),
    cardStyle(index) {
      if (this.isExpanded) {
        return {
          transform: 'translateY(0px) scale(1)',
          zIndex: 100 - index,
        }
      }

      const offset = index * 100
      const scale = 1 - index * 0.03
      const opacity = 1 - index * 0.08

      return {
        transform: `
          translateY(${offset}px)
          scale(${scale})
        `,
        opacity,
        zIndex: 100 - index,
      }
    },
    showExpanded(index) {
      if (index !== 0) {
        return
      }
      this.isExpanded = true
    },
    collapseStack() {
      const list = this.$refs['export-list']?.$el

      if (list) {
        list.scrollTo({
          top: 0,
        })
      }

      this.isExpanded = false
    },
    async remove(id) {
      this.loadingRemove = true
      await this.removeExport({
        id: this.referralProgramId,
        exportId: id,
      })
      this.loadingRemove = false
    },
    runExport(item) {
      this.downloadExport({
        id: this.referralProgramId,
        exportId: item.id,
      })
    },
    handleExportProgress(payload) {
      if (!payload) {
        return
      }

      this.$store.commit('exports/UPDATE_EXPORT', payload)

      this.$refs['export-modal'] && this.$refs['export-modal'].updateFileStatus(payload)
    },
    subscribe(id) {
      exportReferralProgramSocket.subscribe(id, {
        onMessage: this.handleExportProgress,
      })
    },
    unsubscribe() {
      exportReferralProgramSocket.unsubscribe()
    },
  },
  mounted() {
    const accessToken = localStorage.getItem('accessToken')

    if (!accessToken) {
      return
    }

    initEcho(accessToken)
  },
  beforeDestroy() {
    this.unsubscribe()
    destroyEcho()
  },
}
</script>

<style scoped lang="scss">
.export-stack {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 340px;
  z-index: 99;
  height: auto;

  &__container {
    width: 100%;
    overflow: visible;

    &--expanded > div {
      overflow-y: scroll;
      max-height: 400px;
      background: rgba(0, 0, 0, 0.02);
      backdrop-filter: blur(12px);
      border-radius: 8px;
      padding: 10px;
      margin: 0;
    }
  }

  &__list {
    display: flex;
    max-height: 75px;
    flex-direction: column-reverse;
    gap: 12px;
    margin: 10px;
  }
}

.stack-move {
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.stack-enter-active {
  transition:
    opacity 0.35s ease,
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.stack-enter {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.stack-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.35s ease;

  position: absolute;
  width: 100%;
}

.stack-leave-to {
  opacity: 0;
  transform:
    translateX(120px)
    scale(0.9);
}
</style>
